import { amountWithCurrency } from 'modules/finance/lib/budgets'

export const EXPENSE_SUMMARIES_CONFIG: { [key: string]: { type: 'sum' } } = {
  amount: { type: 'sum' },
  reimbursableAmount: { type: 'sum' },
}

type NodeWithCurrency = {
  company?: { currency?: string | null } | null
  currency: string
}

export function formatExpenseSummary<T extends NodeWithCurrency>(
  connection: {
    nodes: T[]
    aggregate?: {
      sum?: { amount?: number | string | null; reimbursableAmount?: number | string | null } | null
      terms?: {
        currency?: { values?: string[] | null } | null
        reimbursableCurrency?: { values?: string[] | null } | null
      } | null
    } | null
  },
  locale: string
): (T & { amount: string; reimbursableAmount: string }) | undefined {
  const { nodes, aggregate } = connection
  if (!nodes[0] || !aggregate?.sum?.amount) return undefined

  // Get all unique currencies from the aggregate terms (includes ALL expenses, not just current page)
  const allCurrencies = aggregate.terms?.currency?.values ?? []

  // If backend returns empty array for terms.currency.values, we CANNOT trust the aggregate
  // because we don't know if there are multiple currencies across all pages
  if (allCurrencies.length === 0) {
    return undefined
  }

  // Check for multiple currencies using the aggregate terms data
  if (allCurrencies.length > 1) {
    return undefined
  }

  // Single currency confirmed across ALL expenses
  const currency = allCurrencies[0]
  if (!currency) return undefined

  const formattedAmount = amountWithCurrency(Number(aggregate.sum.amount), currency, locale)

  // Reimbursable amount uses its own currency (can differ from expense currency, e.g. when
  // the expense is in a different currency than the group or the employee's legal entity).
  // Only show the sum when all reimbursable expenses share exactly one reimbursable currency.
  // NULL reimbursable_currency rows (non-reimbursable expenses) are excluded from the terms
  // by the backend aggregation, so they don't affect the single-currency check.
  const allReimbursableCurrencies = aggregate.terms?.reimbursableCurrency?.values ?? []
  const reimbursableCurrency =
    allReimbursableCurrencies.length === 1 ? allReimbursableCurrencies[0] : undefined
  const reimbursableAmountRaw = aggregate.sum.reimbursableAmount
  const formattedReimbursableAmount =
    reimbursableCurrency != null && reimbursableAmountRaw != null
      ? amountWithCurrency(Number(reimbursableAmountRaw), reimbursableCurrency, locale)
      : ''

  return {
    ...nodes[0],
    amount: formattedAmount,
    reimbursableAmount: formattedReimbursableAmount,
  }
}
